import { useRef, useEffect, useState, memo } from "react";

interface LazyVideoProps {
  src: string;
  className?: string;
  poster?: string;
  width?: number;
  height?: number;
  priority?: boolean;
}

/**
 * LazyVideo - Performance-optimized video component
 * 
 * Key optimizations:
 * - Desktop: Larger preload margin for smoother UX
 * - Mobile: Smaller margin to save data
 * - Only loads video when visible (saves bandwidth)
 * - Pauses when not visible (saves battery/CPU)
 * - Uses preload="none" until visible
 * - Memoized to prevent unnecessary re-renders
 * - Properly handles src changes for feature switching
 * - CLS prevention with fixed aspect ratio container
 */
function LazyVideoComponent({ 
  src, 
  className = "", 
  poster, 
  width, 
  height,
  priority = false 
}: LazyVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(priority);
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentSrc, setCurrentSrc] = useState<string | null>(null);

  // Set up intersection observer for lazy loading
  useEffect(() => {
    if (priority) {
      setIsVisible(true);
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    // Device-aware rootMargin
    // Desktop: Larger margin for smoother loading
    // Mobile: Smaller margin to save data
    const isDesktop = window.innerWidth >= 1024;
    const isMobile = window.innerWidth < 768;
    const rootMargin = isDesktop ? "200px" : isMobile ? "25px" : "100px";

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
        });
      },
      {
        rootMargin,
        threshold: 0.1,
      }
    );

    observer.observe(container);

    return () => observer.disconnect();
  }, [priority]);

  // Handle src changes and visibility
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isVisible) return;

    // Only update if src actually changed
    if (currentSrc !== src) {
      video.src = src;
      video.load();
      setCurrentSrc(src);
      setIsLoaded(false);
      
      video.play().catch(() => {
        // Autoplay might be blocked, that's ok
      });
    }
  }, [isVisible, src, currentSrc]);

  // Pause video when not visible to save resources
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (!isVisible && currentSrc) {
      video.pause();
    } else if (isVisible && currentSrc) {
      video.play().catch(() => {});
    }
  }, [isVisible, currentSrc]);

  const handleLoadedData = () => {
    setIsLoaded(true);
  };

  return (
    <div 
      ref={containerRef}
      className="relative bg-muted/20" 
      style={{ 
        aspectRatio: width && height ? `${width} / ${height}` : '1 / 1',
        contain: 'layout paint style',
      }}
    >
      {/* Loading skeleton - shown until video loads */}
      {!isLoaded && (
        <div 
          className="absolute inset-0 skeleton-loading rounded-lg"
          aria-hidden="true"
        />
      )}
      <video
        ref={videoRef}
        className={`absolute inset-0 w-full h-full transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'} ${className}`}
        autoPlay
        loop
        muted
        playsInline
        poster={poster}
        width={width}
        height={height}
        preload={priority ? "auto" : "none"}
        onLoadedData={handleLoadedData}
      />
    </div>
  );
}

export const LazyVideo = memo(LazyVideoComponent);
export default LazyVideo;
